// CB:BEGIN admin-priority2-project-management-component
/**
 * Project Management Actions Component
 * Allows admins to delete, reassign, archive, and unarchive projects
 */
import React, { useState } from 'react';
import { useApi } from '../../lib/useApi';

interface ProjectManagementActionsProps {
  projectId: string;
  projectAddress: string;
  currentStatus: string;
  currentHomeownerId?: string;
  isArchived?: boolean;
  onSuccess: () => void;
}

export const ProjectManagementActions: React.FC<ProjectManagementActionsProps> = ({
  projectId,
  projectAddress,
  currentStatus,
  isArchived = false,
  onSuccess
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [reassignHomeownerId, setReassignHomeownerId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [archiveReason, setArchiveReason] = useState('');

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      alert('Deletion reason required');
      return;
    }

    if (!window.confirm(`⚠️ PERMANENTLY DELETE project "${projectAddress}"?\n\nThis action:\n- Sets status to CANCELLED\n- Preserves data for audit trail\n- Cannot be undone\n\nContinue?`)) {
      return;
    }

    setLoading(true);
    try {
      // CB:BEGIN admin-projects-soft-delete-api
      await api.delete(`/api/admin/projects/${projectId}`, { reason: deleteReason });
      // CB:END admin-projects-soft-delete-api
      alert('✅ Project deleted (soft delete, data preserved)');
      setShowDeleteModal(false);
      setDeleteReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to delete project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignHomeownerId.trim()) {
      alert('New homeowner ID required');
      return;
    }

    if (!reassignReason.trim()) {
      alert('Reassignment reason required');
      return;
    }

    if (!window.confirm(`Reassign project to homeowner ${reassignHomeownerId}?`)) {
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/admin/projects/${projectId}/reassign`, {
        newHomeownerId: reassignHomeownerId,
        reason: reassignReason
      });
      alert('✅ Project reassigned successfully');
      setShowReassignModal(false);
      setReassignHomeownerId('');
      setReassignReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to reassign project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveReason.trim()) {
      alert('Archive reason required');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/admin/projects/${projectId}/archive`, {
        reason: archiveReason
      });
      alert('✅ Project archived');
      setShowArchiveModal(false);
      setArchiveReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to archive project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async () => {
    const reason = window.prompt('Reason for unarchiving project:');
    if (!reason) return;

    setLoading(true);
    try {
      await api.post(`/api/admin/projects/${projectId}/unarchive`, { reason });
      alert('✅ Project unarchived');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to unarchive project'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{
        padding: '16px',
        background: '#fef3c7',
        borderRadius: '8px',
        marginBottom: '16px',
        border: '1px solid #fbbf24'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
          Project Management Actions
        </h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#78350f' }}>
          ⚠️ Use with caution - these actions affect project ownership and visibility
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={loading || currentStatus === 'CANCELLED'}
            style={{
              padding: '8px 16px',
              background: currentStatus === 'CANCELLED' ? '#e5e7eb' : '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: currentStatus === 'CANCELLED' ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {currentStatus === 'CANCELLED' ? 'Already Deleted' : 'Delete Project'}
          </button>

          <button
            onClick={() => setShowReassignModal(true)}
            disabled={loading || currentStatus === 'CANCELLED'}
            style={{
              padding: '8px 16px',
              background: currentStatus === 'CANCELLED' ? '#e5e7eb' : '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: currentStatus === 'CANCELLED' ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Reassign Homeowner
          </button>

          {!isArchived ? (
            <button
              onClick={() => setShowArchiveModal(true)}
              disabled={loading || currentStatus !== 'COMPLETED'}
              style={{
                padding: '8px 16px',
                background: currentStatus !== 'COMPLETED' ? '#e5e7eb' : '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: currentStatus !== 'COMPLETED' ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              title={currentStatus !== 'COMPLETED' ? 'Only completed projects can be archived' : ''}
            >
              Archive Project
            </button>
          ) : (
            <button
              onClick={handleUnarchive}
              disabled={loading}
              style={{
                padding: '8px 16px',
                background: '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Unarchive Project
            </button>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>
              Delete Project
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
              Project: <strong>{projectAddress}</strong>
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              This will set the project status to CANCELLED and preserve all data for audit purposes.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Deletion Reason (Required)
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Why is this project being deleted?"
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading || !deleteReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !deleteReason.trim() ? '#9ca3af' : '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !deleteReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Modal */}
      {showReassignModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowReassignModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Reassign Project
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
              Project: <strong>{projectAddress}</strong>
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                New Homeowner ID (Required)
              </label>
              <input
                type="text"
                value={reassignHomeownerId}
                onChange={(e) => setReassignHomeownerId(e.target.value)}
                placeholder="Enter homeowner user ID"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Reassignment Reason (Required)
              </label>
              <textarea
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="Why is this project being reassigned?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowReassignModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReassign}
                disabled={loading || !reassignHomeownerId.trim() || !reassignReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !reassignHomeownerId.trim() || !reassignReason.trim() ? '#9ca3af' : '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !reassignHomeownerId.trim() || !reassignReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Reassigning...' : 'Reassign Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowArchiveModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Archive Project
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
              Project: <strong>{projectAddress}</strong>
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Archiving will set completedAt timestamp and mark the project as archived.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Archive Reason (Required)
              </label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="Why is this project being archived?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowArchiveModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                disabled={loading || !archiveReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !archiveReason.trim() ? '#9ca3af' : '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !archiveReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Archiving...' : 'Archive Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
// CB:END admin-priority2-project-management-component
